import React, { useEffect, useState } from 'react';
import { BookOpen, Download, FileText, Image, ChevronDown, ChevronUp, Calendar } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { bestPracticesAPI } from '../lib/api';
import { formatDateTime } from '../lib/utils';
import { toast } from 'sonner';
import { Header } from '../components/layout/Header';
import { Footer } from '../components/layout/Footer';

const BP_CATEGORIES = [
    { id: 'all', name: 'Toutes les catégories' },
    { id: 'fruits', name: 'Fruits' },
    { id: 'legumes', name: 'Légumes' },
    { id: 'plantes', name: 'Plantes & Fleurs' },
    { id: 'cosmetiques', name: 'Cosmétiques' },
    { id: 'general', name: 'Général' },
];

const BACKEND_URL = window.location.origin;

export const ClientBestPractices = () => {
    const [bestPractices, setBestPractices] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedCategory, setSelectedCategory] = useState('all');
    const [expandedIds, setExpandedIds] = useState(new Set());

    useEffect(() => {
        const fetchData = async () => {
            try {
                const res = await bestPracticesAPI.getMine();
                setBestPractices(res.data.best_practices || []);
            } catch (error) {
                console.error('Error:', error);
                toast.error('Erreur lors du chargement');
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, []);

    const toggleExpand = (id) => {
        setExpandedIds(prev => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    };

    const filteredBPs = selectedCategory === 'all'
        ? bestPractices
        : bestPractices.filter(bp => bp.category === selectedCategory);

    const getCategoryName = (catId) => BP_CATEGORIES.find(c => c.id === catId)?.name || catId;

    const getCategoryColor = (catId) => {
        const colors = {
            fruits: 'bg-orange-100 text-orange-800 border-orange-200',
            legumes: 'bg-green-100 text-green-800 border-green-200',
            plantes: 'bg-pink-100 text-pink-800 border-pink-200',
            cosmetiques: 'bg-purple-100 text-purple-800 border-purple-200',
            general: 'bg-blue-100 text-blue-800 border-blue-200',
        };
        return colors[catId] || colors.general;
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-sand flex items-center justify-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-jungle"></div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-sand flex flex-col" data-testid="client-best-practices">
            <Header />
            <main className="flex-1 max-w-4xl mx-auto w-full px-4 py-8">
                <div className="mb-8">
                    <div className="flex items-center gap-3 mb-2">
                        <div className="p-2 bg-jungle/10 rounded-xl">
                            <BookOpen className="h-6 w-6 text-jungle" />
                        </div>
                        <h1 className="font-heading text-3xl font-bold tracking-tight text-jungle">
                            Bonnes Pratiques
                        </h1>
                    </div>
                    <p className="text-muted-foreground">
                        Guides et conseils professionnels mis à votre disposition par PRIXDEAL
                    </p>
                </div>

                {/* Filter */}
                <div className="mb-6">
                    <Select value={selectedCategory} onValueChange={setSelectedCategory}>
                        <SelectTrigger className="w-64" data-testid="bp-filter">
                            <SelectValue placeholder="Filtrer par catégorie" />
                        </SelectTrigger>
                        <SelectContent>
                            {BP_CATEGORIES.map(c => (
                                <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>

                {filteredBPs.length === 0 ? (
                    <Card>
                        <CardContent className="py-12 text-center">
                            <BookOpen className="h-16 w-16 text-muted-foreground mx-auto mb-4 opacity-50" />
                            <p className="text-muted-foreground text-lg mb-2">Aucun guide disponible</p>
                            <p className="text-sm text-muted-foreground">
                                Les guides publiés par PRIXDEAL pour vous apparaîtront ici
                            </p>
                        </CardContent>
                    </Card>
                ) : (
                    <div className="space-y-4">
                        {filteredBPs.map((bp) => {
                            const isExpanded = expandedIds.has(bp.id);
                            return (
                                <Card key={bp.id} className="overflow-hidden hover:shadow-md transition-shadow" data-testid={`bp-card-${bp.id}`}>
                                    <div className="cursor-pointer" onClick={() => toggleExpand(bp.id)}>
                                        <CardHeader className="pb-3">
                                            <div className="flex items-start justify-between">
                                                <div className="flex-1">
                                                    <div className="flex items-center gap-2 mb-2">
                                                        <Badge className={getCategoryColor(bp.category)}>
                                                            {getCategoryName(bp.category)}
                                                        </Badge>
                                                        <div className="flex items-center gap-1 text-xs text-muted-foreground">
                                                            <Calendar className="h-3 w-3" />
                                                            {formatDateTime(bp.created_at)}
                                                        </div>
                                                    </div>
                                                    <CardTitle className="text-xl text-jungle">{bp.title}</CardTitle>
                                                    {!isExpanded && (
                                                        <CardDescription className="mt-1 line-clamp-2">
                                                            {bp.content}
                                                        </CardDescription>
                                                    )}
                                                </div>
                                                <Button variant="ghost" size="icon" className="shrink-0">
                                                    {isExpanded ? <ChevronUp className="h-5 w-5" /> : <ChevronDown className="h-5 w-5" />}
                                                </Button>
                                            </div>
                                            {!isExpanded && (bp.image_urls?.length > 0 || bp.pdf_url) && (
                                                <div className="flex items-center gap-2 mt-2">
                                                    {bp.image_urls?.length > 0 && (
                                                        <Badge variant="secondary" className="text-xs">
                                                            <Image className="h-3 w-3 mr-1" />{bp.image_urls.length} image(s)
                                                        </Badge>
                                                    )}
                                                    {bp.pdf_url && (
                                                        <Badge variant="secondary" className="text-xs">
                                                            <FileText className="h-3 w-3 mr-1" />PDF joint
                                                        </Badge>
                                                    )}
                                                </div>
                                            )}
                                        </CardHeader>
                                    </div>

                                    {isExpanded && (
                                        <CardContent className="pt-0 space-y-4">
                                            {/* Content */}
                                            <div className="prose prose-sm max-w-none bg-white rounded-lg p-4 border">
                                                {bp.content.split('\n').map((line, i) => (
                                                    <p key={`${bp.id}-line-${i}`} className={`${line.trim() === '' ? 'mb-2' : 'mb-1'} text-sm text-gray-700`}>
                                                        {line || '\u00A0'}
                                                    </p>
                                                ))}
                                            </div>

                                            {/* Images */}
                                            {bp.image_urls?.length > 0 && (
                                                <div>
                                                    <h4 className="font-medium text-sm text-jungle mb-2 flex items-center gap-1">
                                                        <Image className="h-4 w-4" /> Images
                                                    </h4>
                                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                                        {bp.image_urls.map((url, idx) => (
                                                            <a key={url} href={`${BACKEND_URL}${url}`} target="_blank" rel="noopener noreferrer" className="block">
                                                                <img
                                                                    src={`${BACKEND_URL}${url}`}
                                                                    alt={`Guide ${bp.title} - Image ${idx + 1}`}
                                                                    className="w-full rounded-lg border shadow-sm hover:shadow-md transition-shadow cursor-pointer"
                                                                />
                                                            </a>
                                                        ))}
                                                    </div>
                                                </div>
                                            )}

                                            {/* PDF */}
                                            {bp.pdf_url && (
                                                <div>
                                                    <a href={`${BACKEND_URL}${bp.pdf_url}`} target="_blank" rel="noopener noreferrer">
                                                        <Button variant="outline" className="gap-2" data-testid={`bp-download-pdf-${bp.id}`}>
                                                            <Download className="h-4 w-4" />
                                                            Télécharger le PDF : {bp.pdf_filename || 'document.pdf'}
                                                        </Button>
                                                    </a>
                                                </div>
                                            )}
                                        </CardContent>
                                    )}
                                </Card>
                            );
                        })}
                    </div>
                )}
            </main>
            <Footer />
        </div>
    );
};
